import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { subCategoryAPI, vendorAPI } from '../lib/api';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const priceTiers = ['Budget', 'Mid', 'Premium'];

const paxOptions = [
  { value: 'A', label: 'Pax A (100-200 people)' },
  { value: 'B', label: 'Pax B (200-400 people)' },
  { value: 'C', label: 'Pax C (400-500 people)' },
  { value: 'D', label: 'Pax D (500-1000 people)' },
  { value: 'E', label: 'Pax E (more than 1000 people)' },
];

const getPaxLabel = (pax) => {
  const option = paxOptions.find(p => p.value === pax);
  return option ? option.label : pax;
};

const PriceTierBadge = ({ tier }) => {
  const colors = {
    Budget: 'bg-[#E8F5E9] text-[#2E7D32]',
    Mid: 'bg-[#FFF3E0] text-[#E65100]',
    Premium: 'bg-[#FCE4EC] text-[#C2185B]',
  };
  
  return (
    <span className={`px-3 py-1 text-xs font-medium uppercase tracking-wide rounded-sm ${colors[tier] || 'bg-gray-100 text-gray-600'}`}>
      {tier}
    </span>
  );
};

const PaxBadge = ({ pax }) => {
  return (
    <span className="px-2 py-1 text-xs font-medium bg-[#F2EBE3] text-[#69111F] rounded-sm">
      Pax {pax}
    </span>
  );
};

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [filterPax, setFilterPax] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    sub_category_id: '',
    contact: '',
    email: '',
    price_tier: 'Mid',
    pax: 'A',
    min_price: 0,
    max_price: 0,
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subCategoriesRes, vendorsRes] = await Promise.all([
        subCategoryAPI.getAll(),
        vendorAPI.getAll(),
      ]);
      setSubCategories(subCategoriesRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vendor = null) => {
    if (vendor) {
      setSelectedVendor(vendor);
      setFormData({
        name: vendor.name,
        sub_category_id: vendor.sub_category_id,
        contact: vendor.contact,
        email: vendor.email,
        price_tier: vendor.price_tier,
        pax: vendor.pax || 'A',
        min_price: vendor.min_price,
        max_price: vendor.max_price,
        notes: vendor.notes,
      });
    } else {
      setSelectedVendor(null);
      setFormData({
        name: '',
        sub_category_id: subCategories[0]?.id || '',
        contact: '',
        email: '',
        price_tier: 'Mid',
        pax: 'A',
        min_price: 0,
        max_price: 0,
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVendor(null);
    setFormData({
      name: '',
      sub_category_id: '',
      contact: '',
      email: '',
      price_tier: 'Mid',
      pax: 'A',
      min_price: 0,
      max_price: 0,
      notes: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    if (!formData.sub_category_id) {
      toast.error('Please select a sub-category');
      return;
    }

    if (!formData.contact.trim()) {
      toast.error('Contact is required');
      return;
    }

    setSaving(true);
    try {
      if (selectedVendor) {
        await vendorAPI.update(selectedVendor.id, formData);
        toast.success('Vendor updated successfully');
      } else {
        await vendorAPI.create(formData);
        toast.success('Vendor created successfully');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVendor) return;

    try {
      await vendorAPI.delete(selectedVendor.id);
      toast.success('Vendor deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
    }
  };

  const openDeleteDialog = (vendor) => {
    setSelectedVendor(vendor);
    setDeleteDialogOpen(true);
  };

  let filteredVendors = vendors;
  if (filterSubCategory !== 'all') {
    filteredVendors = filteredVendors.filter(v => v.sub_category_id === filterSubCategory);
  }
  if (filterPax !== 'all') {
    filteredVendors = filteredVendors.filter(v => v.pax === filterPax);
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="vendors-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
              Vendor Registry
            </h1>
            <p className="text-[#585858] mt-2">
              Manage vendors and their pricing information
            </p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            disabled={subCategories.length === 0}
            className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm"
            data-testid="add-vendor-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Vendor
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-[#585858]">Sub-Category:</Label>
            <Select value={filterSubCategory} onValueChange={setFilterSubCategory}>
              <SelectTrigger className="w-56 border-[#E6DCD3]" data-testid="filter-subcategory-select">
                <SelectValue placeholder="All Sub-Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sub-Categories</SelectItem>
                {subCategories.map((sc) => (
                  <SelectItem key={sc.id} value={sc.id}>{sc.name} ({sc.category_name})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-[#585858]">Pax:</Label>
            <Select value={filterPax} onValueChange={setFilterPax}>
              <SelectTrigger className="w-56 border-[#E6DCD3]" data-testid="filter-pax-select">
                <SelectValue placeholder="All Pax" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pax</SelectItem>
                {paxOptions.map((pax) => (
                  <SelectItem key={pax.value} value={pax.value}>{pax.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E6DCD3] rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#69111F]" />
            </div>
          ) : subCategories.length === 0 ? (
            <div className="p-12 text-center text-[#585858]">
              <p>Please create sub-categories first before adding vendors.</p>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="p-12 text-center text-[#585858]">
              <p>No vendors found. Create your first vendor to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EBE3] hover:bg-[#F2EBE3]">
                  <TableHead className="font-serif text-[#69111F] font-medium">Vendor Name</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Sub-Category</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Contact</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Pax</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Price Tier</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium text-right">Price Range</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id} className="hover:bg-[#FDFBF8]">
                    <TableCell className="font-medium text-[#1A1A1A]">{vendor.name}</TableCell>
                    <TableCell className="text-[#585858]">{vendor.sub_category_name}</TableCell>
                    <TableCell className="text-[#585858]">{vendor.contact}</TableCell>
                    <TableCell>
                      <PaxBadge pax={vendor.pax || 'A'} />
                    </TableCell>
                    <TableCell>
                      <PriceTierBadge tier={vendor.price_tier} />
                    </TableCell>
                    <TableCell className="text-right font-serif text-[#69111F]">
                      Rp {vendor.min_price.toLocaleString()} - Rp {vendor.max_price.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(vendor)}
                          className="text-[#69111F] hover:bg-[#69111F]/10"
                          data-testid={`edit-vendor-${vendor.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(vendor)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-vendor-${vendor.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#1A1A1A]">
                {selectedVendor ? 'Edit Vendor' : 'Add Vendor'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#1A1A1A]">Vendor Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Elegant Florals"
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="vendor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subCategory" className="text-[#1A1A1A]">Sub-Category *</Label>
                  <Select 
                    value={formData.sub_category_id} 
                    onValueChange={(value) => setFormData({ ...formData, sub_category_id: value })}
                  >
                    <SelectTrigger className="border-[#E6DCD3]" data-testid="vendor-subcategory-select">
                      <SelectValue placeholder="Select a sub-category" />
                    </SelectTrigger>
                    <SelectContent>
                      {subCategories.map((sc) => (
                        <SelectItem key={sc.id} value={sc.id}>{sc.name} ({sc.category_name})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact" className="text-[#1A1A1A]">Contact *</Label>
                  <Input
                    id="contact"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    placeholder="Phone number or contact info"
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="vendor-contact-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1A1A1A]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="vendor@example.com"
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="vendor-email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pax" className="text-[#1A1A1A]">Pax *</Label>
                    <Select 
                      value={formData.pax} 
                      onValueChange={(value) => setFormData({ ...formData, pax: value })}
                    >
                      <SelectTrigger className="border-[#E6DCD3]" data-testid="vendor-pax-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paxOptions.map((pax) => (
                          <SelectItem key={pax.value} value={pax.value}>{pax.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priceTier" className="text-[#1A1A1A]">Price Tier *</Label>
                    <Select 
                      value={formData.price_tier} 
                      onValueChange={(value) => setFormData({ ...formData, price_tier: value })}
                    >
                      <SelectTrigger className="border-[#E6DCD3]" data-testid="vendor-pricetier-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {priceTiers.map((tier) => (
                          <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minPrice" className="text-[#1A1A1A]">Min Price (Rp)</Label>
                    <Input
                      id="minPrice"
                      type="number"
                      min="0"
                      value={formData.min_price}
                      onChange={(e) => setFormData({ ...formData, min_price: parseFloat(e.target.value) || 0 })}
                      className="border-[#E6DCD3] focus:border-[#69111F]"
                      data-testid="vendor-minprice-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxPrice" className="text-[#1A1A1A]">Max Price (Rp)</Label>
                    <Input
                      id="maxPrice"
                      type="number"
                      min="0"
                      value={formData.max_price}
                      onChange={(e) => setFormData({ ...formData, max_price: parseFloat(e.target.value) || 0 })}
                      className="border-[#E6DCD3] focus:border-[#69111F]"
                      data-testid="vendor-maxprice-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-[#1A1A1A]">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes about this vendor"
                    className="border-[#E6DCD3] focus:border-[#69111F] min-h-[80px]"
                    data-testid="vendor-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="border-[#E6DCD3]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8]"
                  data-testid="save-vendor-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {selectedVendor ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-[#1A1A1A]">
                Delete Vendor
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#585858]">
                Are you sure you want to delete "{selectedVendor?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#E6DCD3]">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-vendor-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default Vendors;
